import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router";
import Div from "../common/components/div";
import { FullPageSpinner } from "../common/components/FullPageSpinner";
import H1 from "../common/components/h1";
import translate from "../common/functions/translate";
import useCheckForSession from "../common/hooks/useCheckForSession";
import { usePageTitle } from "../common/hooks/usePageTitle";
import { SubmitButton } from "./buttons/Buttons";
import { VerifyPassword } from "./VerifyPassword";
import { SecurityCodeMethod, useMfaApi } from "./hooks/useMfaApi";
import { MfaHelpDrawer } from "./shared-components/MfaHelpDrawer";
import BackNav from '../common/components/BackNav';
import { mfaPageView, mfaSetupStart } from "../common/analytics";
import { useMfaInlineError } from "../common/hooks/use.analytics";
import useCheckForUpdateAccountCookie from "../common/hooks/useCheckForUpdateAccountCookie";

interface ChooseYourMethodProps {
    flow: 'add' | 'enroll'
}

export const ChooseYourMethod = ({ flow }: ChooseYourMethodProps) => {
    const [securityCodeMethod, setSecurityCodeMethod] = useState<SecurityCodeMethod | null>(null);
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const api = useMfaApi();
    const history = useHistory();
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [isCheckingCookie, setIsCheckingCookie] = useState(true);
    usePageTitle(translate(`mfa_title_${flow}`));
    useCheckForSession(setIsCheckingSession);
    useCheckForUpdateAccountCookie(setIsCheckingCookie, setIsVerified, isCheckingSession);
    const choicesLabel = translate("select_security_codes");
    const inlineError = useMfaInlineError(choicesLabel);

    useEffect(() => {
        if (isVerified) {
            mfaPageView(`my account: mfa: ${flow}: select how to get security code`);
        }
    }, [flow, isVerified]);

    const verifyRequestMade = useRef(false);
    useEffect(() => {
        if (verifyRequestMade.current) {
            return;
        }
        verifyRequestMade.current = true;
        verify();
        async function verify() {
            const verifyResponse = await api.mfaVerifyCookie();
            setIsVerified(verifyResponse?.status === 200);
        }
    }, [setIsVerified, api]);

    useEffect(() => {
        if (securityCodeMethod) {
            setFieldError(null);
        }
    }, [securityCodeMethod]);

    async function onContinue(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!securityCodeMethod) {
            const errMessage = translate("select_your_preferred_method");
            setFieldError(errMessage);
            inlineError.validationError(errMessage);
            return;
        }
        mfaSetupStart(securityCodeMethod);
        history.push('/security-code/enroll/enter-your-info', {
            type: securityCodeMethod,
            flow: flow
        });
    }

    if (isCheckingSession || isCheckingCookie) {
        return <FullPageSpinner />;
    }

    if (!isVerified) {
        return <VerifyPassword flow={flow} type={null} onVerified={() => setIsVerified(true)}/>
    }

    const bodyText = (flow === 'add') ? 'add_another_method_to_your_account' : 'get_unique_security_code';
    const hintText = (flow === 'add') ? null : translate('you_can_add_method');
    const headingTitle = translate(`mfa_title_${flow}`);
    return (<>
        <BackNav />
        <Div isContainer paddingy={1} paddingyMD={2}>
            <form onSubmit={onContinue} method="POST">
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <H1 className={'ds-u-margin-bottom--1'}>
                            {headingTitle}
                        </H1>
                        <p className="ds-u-margin-bottom--1">{translate(bodyText)}</p>
                        <MfaHelpDrawer toggleClassName="ds-u-margin-bottom--5" />
                        <ChoiceList
                            label={
                                <h2 className= {"ds-text-heading--xl ds-u-margin-bottom--1"} style={{ textTransform: 'none' }}>
                                    {choicesLabel}
                                </h2>
                            }
                            hint={hintText}
                            labelClassName={"ds-u-measure--wide"}
                            className={'mfa-method-list ds-u-margin-bottom--3'}
                            name={'selectSecurityMethod'}
                            onChange={(e) => setSecurityCodeMethod(e.target.value)}
                            type={'radio'}
                            choices={[
                                {
                                    className: 'ds-u-margin-bottom--2 ds-u-margin-top--0',
                                    label: translate('text_message'),
                                    value: 'sms'
                                },
                                {
                                    className: 'ds-u-margin-bottom--2',
                                    label: translate('email_address'),
                                    value: 'email'
                                },
                                {
                                    label: translate('phone_call'),
                                    value: 'voice'
                                }
                            ]}
                            errorMessage={fieldError}
                        />

                    </Div>
                </Div>
                <SubmitButton mfaAnalyticsData={{
                    text: translate('continue'),
                    parentComponentHeading: headingTitle,
                }}>
                    {translate('continue')}
                </SubmitButton>
            </form>
        </Div>
    </>);
}